import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faDollarSign,
  faTag,
  faFile,
  faUpload,
  faSpinner,
  faCheck,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faArrowsRotate,
  faCheckCircle,
  faTimes,
  faPlus,
  faTrash,
  faPen,
  faInfoCircle,
  faCloudArrowUp,
  faFileLines,
  faRobot,
  faClipboardCheck,
  faExpand,
  faCompress,
  faLayerGroup,
  faListOl,
} from '@fortawesome/free-solid-svg-icons';

import { api } from '@/lib/axios';
import { API_ROUTES, DEFAULT_CITY, DEFAULT_COUNTRY, LOGO, PROJECT_NAME } from '@/constants';
import { useCountries, useCities, useDistricts } from '@/api/useLocations';
import { Button } from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { useModalStore } from '@/stores';
import type { ProposedCost, ProposedSpec, ProviderProposal, ProviderContact, SavedDocument } from '@/types/provider';
import { GateModal, type ExternalCreator } from './GateModal';
import styles from './ExternalProviderSubmit.module.css';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface DocumentAnalysis {
  localId: string;
  file: File;
  status: 'uploading' | 'analyzing' | 'done' | 'error';
  gcsUrl?: string;
  filename: string;
  summary?: string;
  proposals?: ProviderProposal;
  reviewOpen: boolean;
  applied: boolean;
  fieldAcceptance: Record<string, boolean>;
  fieldDismissed: Record<string, boolean>;
  serviceAcceptance: Record<number, boolean>;
  error?: string;
  analysisStartTime?: number;
}

interface AcceptedService {
  localId: string;
  serviceName: string;
  serviceId: string | null;
  description: string;
  costs: ProposedCost[];
  specs: ProposedSpec[];
  isFromDocument?: boolean;
}

type PageState = 'form' | 'submitting' | 'thanks';

function uid() {
  return Math.random().toString(36).slice(2);
}

// ─────────────────────────────────────────────
// Submit Confirmation Modal
// ─────────────────────────────────────────────

interface SubmitConfirmModalProps {
  providerName: string;
  serviceCount: number;
  documentCount: number;
  onConfirm: () => Promise<void>;
  handleClose?: () => void;
}

function SubmitConfirmModal({
  providerName,
  serviceCount,
  documentCount,
  onConfirm,
  handleClose,
}: SubmitConfirmModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitClick = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.confirmModal}>
      <h2 className={styles.confirmTitle}>Submit Provider?</h2>
      <p className={styles.confirmSubtitle}>
        The following information will be submitted for review:
      </p>
      <ul className={styles.confirmList}>
        <li>
          <FontAwesomeIcon icon={faCheckCircle} className={styles.confirmListIcon} />{' '}
          <strong>{providerName}</strong>
        </li>
        <li>
          <FontAwesomeIcon icon={faCheckCircle} className={styles.confirmListIcon} /> {serviceCount}{' '}
          service{serviceCount !== 1 ? 's' : ''}
        </li>
        <li>
          <FontAwesomeIcon icon={faCheckCircle} className={styles.confirmListIcon} />{' '}
          {documentCount} document{documentCount !== 1 ? 's' : ''}
        </li>
      </ul>
      <p className={styles.confirmNote}>
        Our team will review the submission and reach out if needed.
      </p>
      <div className={styles.confirmActions}>
        <Button variant="secondary" size="md" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={handleSubmitClick} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} /> Submit
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DocumentCard sub-component
// ─────────────────────────────────────────────

function DocumentCard({
  doc,
  onRetry,
  onRemove,
  onToggleReview,
  onToggleField,
  onDismissField,
  onApplyAll,
  onToggleService,
  onApplyField,
}: {
  doc: DocumentAnalysis;
  onRetry: (localId: string) => void;
  onRemove: (localId: string) => void;
  onToggleReview: (localId: string) => void;
  onToggleField: (localId: string, field: string) => void;
  onDismissField: (localId: string, field: string) => void;
  onApplyAll: (doc: DocumentAnalysis) => void;
  onToggleService: (localId: string, idx: number) => void;
  onApplyField: (fieldKey: string, value: string | number | null) => void;
}) {
  const [expandedServices, setExpandedServices] = useState<Set<number>>(new Set());
  const [elapsed, setElapsed] = useState(0);

  const toggleServiceExpand = (idx: number) => {
    setExpandedServices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  useEffect(() => {
    if (doc.status !== 'uploading' && doc.status !== 'analyzing') return;
    const start = doc.analysisStartTime || Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [doc.status, doc.analysisStartTime]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Loading / analyzing
  if (doc.status === 'uploading' || doc.status === 'analyzing') {
    const phases = [
      { label: 'Uploading to cloud', icon: faCloudArrowUp, done: doc.status === 'analyzing' },
      {
        label: 'Extracting content',
        icon: faFileLines,
        done: doc.status === 'analyzing' && elapsed >= 3,
      },
      { label: 'AI analyzing document', icon: faRobot, done: false },
      { label: 'Building proposals', icon: faClipboardCheck, done: false },
    ];
    return (
      <div className={styles.docCard}>
        <div className={styles.analysisProgress}>
          <div className={styles.analysisHeader}>
            <span className={styles.docCardFilename}>{doc.filename}</span>
            <span className={styles.analysisTimer}>{formatTime(elapsed)}</span>
          </div>
          <div className={styles.phaseList}>
            {phases.map((phase, i) => {
              const isActive = !phase.done && (i === 0 || phases[i - 1].done);
              return (
                <div
                  key={i}
                  className={`${styles.phase} ${phase.done ? styles.phaseDone : ''} ${isActive ? styles.phaseActive : ''}`}
                >
                  <div className={styles.phaseIcon}>
                    {phase.done ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : isActive ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={phase.icon} />
                    )}
                  </div>
                  <span className={styles.phaseLabel}>{phase.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (doc.status === 'error') {
    return (
      <div className={`${styles.docCard} ${styles.docCardError}`}>
        <div className={styles.docCardErrorRow}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
          <div className={styles.docCardInfo}>
            <div className={styles.docCardFilename}>{doc.filename}</div>
            <div className={styles.docCardStatus}>{doc.error || 'Analysis failed'}</div>
          </div>
          <div className={styles.docCardActions}>
            <button className={styles.cardBtn} onClick={() => onRetry(doc.localId)}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Retry
            </button>
            <button
              className={`${styles.cardBtn} ${styles.cardBtnDanger}`}
              onClick={() => onRemove(doc.localId)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Done — applied state
  if (doc.applied) {
    return (
      <div className={`${styles.docCard} ${styles.docCardApplied}`}>
        <div className={styles.docCardDoneRow}>
          <FontAwesomeIcon icon={faCheckCircle} className={styles.appliedIcon} />
          <div className={styles.docCardInfo}>
            <div className={styles.docCardFilename}>{doc.filename}</div>
            <div className={styles.docCardStatus}>
              <span className={styles.appliedBadge}>Applied</span>
            </div>
          </div>
          <div className={styles.docCardActions}>
            <button className={styles.cardBtn} onClick={() => onToggleReview(doc.localId)}>
              {doc.reviewOpen ? 'Hide' : 'Review applied'}
            </button>
            <button
              className={`${styles.cardBtn} ${styles.cardBtnDanger}`}
              onClick={() => onRemove(doc.localId)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {doc.reviewOpen && (
          <div className={styles.reviewSection}>
            <div className={styles.appliedReviewNote}>
              This document has been applied. The data below was extracted and added to the form.
            </div>
            {doc.proposals && doc.proposals.services.length > 0 && (
              <div className={styles.reviewServices}>
                <div className={styles.reviewServicesHeader}>
                  Extracted Services ({doc.proposals.services.length})
                </div>
                {doc.proposals.services.map((svc, idx) => (
                  <div key={idx} className={styles.reviewServiceCard}>
                    <div className={styles.reviewServiceHeader}>
                      <div className={styles.reviewServiceNameRow}>
                        <span className={styles.reviewServiceName}>{svc.service_name}</span>
                        {svc.matched ? (
                          <span className={`${styles.matchBadge} ${styles.matchBadgeGreen}`}>
                            <FontAwesomeIcon icon={faCheck} /> matched
                          </span>
                        ) : (
                          <span className={`${styles.matchBadge} ${styles.matchBadgeYellow}`}>
                            new
                          </span>
                        )}
                      </div>
                    </div>
                    {svc.description && (
                      <div className={styles.reviewServiceDesc}>{svc.description}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Done — not yet applied
  const proposals = doc.proposals;
  const fieldEntries: Array<{ key: string; label: string; value: string | number | null }> = [];
  if (proposals) {
    if (proposals.nombre_comercial)
      fieldEntries.push({
        key: 'nombre_comercial',
        label: 'Name',
        value: proposals.nombre_comercial,
      });
    if (proposals.email)
      fieldEntries.push({ key: 'email', label: 'Email', value: proposals.email });
    if (proposals.telefono)
      fieldEntries.push({ key: 'telefono', label: 'Phone', value: proposals.telefono });
    if (proposals.ciudad)
      fieldEntries.push({ key: 'ciudad', label: 'City', value: proposals.ciudad });
    if (proposals.calificacion != null)
      fieldEntries.push({ key: 'calificacion', label: 'Rating', value: proposals.calificacion });
  }

  const visibleFields = fieldEntries.filter((f) => !doc.fieldDismissed[f.key]);
  const fieldCount = fieldEntries.filter((f) => !doc.fieldDismissed[f.key]).length;
  const svcCount = proposals
    ? proposals.services.filter((_, i) => doc.serviceAcceptance[i] !== false).length
    : 0;

  return (
    <div className={`${styles.docCard} ${styles.docCardDone}`}>
      <div className={styles.docCardDoneRow}>
        <FontAwesomeIcon icon={faCheckCircle} className={styles.doneIcon} />
        <div className={styles.docCardInfo}>
          <div className={styles.docCardFilename}>{doc.filename}</div>
          {doc.summary && <div className={styles.docCardSummary}>{doc.summary}</div>}
        </div>
        <div className={styles.docCardActions}>
          <button
            className={`${styles.cardBtn} ${styles.cardBtnDanger}`}
            onClick={() => onRemove(doc.localId)}
            title="Remove document"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {!doc.applied && (
        <button className={styles.applyAllBtn} onClick={() => onApplyAll(doc)}>
          <FontAwesomeIcon icon={faCheck} />
          Apply All Proposals
          <span className={styles.applyAllCount}>
            {fieldCount} fields + {svcCount} services
          </span>
        </button>
      )}

      {doc.proposals && (
        <div className={styles.reviewSection}>
          {/* Field proposals */}
          {visibleFields.length > 0 && (
            <div className={styles.reviewFieldList}>
              {visibleFields.map((f) => {
                const accepted = doc.fieldAcceptance[f.key];
                return (
                  <div key={f.key} className={styles.reviewFieldRow}>
                    <div className={styles.reviewFieldLabel}>{f.label}</div>
                    <div className={styles.createModeField}>
                      <span className={styles.proposedVal}>
                        {f.key === 'calificacion' ? `${f.value} / 5` : String(f.value)}
                      </span>
                      <button
                        className={`${styles.fieldToggleBtn} ${accepted ? styles.fieldToggleBtnOn : ''}`}
                        onClick={() => {
                          onToggleField(doc.localId, f.key);
                          if (!accepted) onApplyField(f.key, f.value);
                        }}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                        {accepted ? 'Applied' : 'Apply'}
                      </button>
                      <button
                        className={styles.fieldDismissBtn}
                        onClick={() => onDismissField(doc.localId, f.key)}
                        title="Dismiss"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Service proposals */}
          {proposals && proposals.services.length > 0 && (
            <div className={styles.reviewServices}>
              <div className={styles.reviewServicesHeader}>
                Proposed Services ({proposals.services.length})
              </div>
              {proposals.services.map((svc, idx) => {
                const accepted = doc.serviceAcceptance[idx] !== false;
                const expanded = expandedServices.has(idx);
                return (
                  <div
                    key={idx}
                    className={`${styles.reviewServiceCard} ${!accepted ? styles.reviewServiceSkipped : ''}`}
                  >
                    <div className={styles.reviewServiceHeader}>
                      <div className={styles.reviewServiceNameRow}>
                        <button
                          className={styles.serviceExpandIcon}
                          onClick={() => toggleServiceExpand(idx)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                          }}
                        >
                          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                        </button>
                        <span className={styles.reviewServiceName}>{svc.service_name}</span>
                        {svc.matched ? (
                          <span className={`${styles.matchBadge} ${styles.matchBadgeGreen}`}>
                            <FontAwesomeIcon icon={faCheck} /> matched
                          </span>
                        ) : (
                          <span className={`${styles.matchBadge} ${styles.matchBadgeYellow}`}>
                            new
                          </span>
                        )}
                      </div>
                      <div className={styles.reviewServiceBtns}>
                        {accepted ? (
                          <button
                            className={`${styles.reviewServiceBtn} ${styles.reviewServiceBtnSkip}`}
                            onClick={() => onToggleService(doc.localId, idx)}
                          >
                            <FontAwesomeIcon icon={faTimes} /> Skip
                          </button>
                        ) : (
                          <button
                            className={`${styles.reviewServiceBtn} ${styles.reviewServiceBtnRestore}`}
                            onClick={() => onToggleService(doc.localId, idx)}
                          >
                            <FontAwesomeIcon icon={faArrowsRotate} /> Restore
                          </button>
                        )}
                      </div>
                    </div>
                    {svc.description && (
                      <div className={styles.reviewServiceDesc}>{svc.description}</div>
                    )}
                    {expanded && (
                      <div className={styles.serviceDetailExpanded}>
                        {svc.costs.length > 0 && (
                          <div className={styles.serviceDetailItem}>
                            <div className={styles.serviceDetailLabel}>Costs</div>
                            <div className={styles.serviceDetailRows}>
                              {svc.costs.map((c, ci) => (
                                <div key={ci} className={styles.serviceDetailRow}>
                                  <FontAwesomeIcon
                                    icon={faDollarSign}
                                    className={styles.serviceDetailIcon}
                                  />
                                  <span className={styles.serviceDetailMain}>
                                    {c.price} {c.currency}
                                  </span>
                                  <span className={styles.serviceDetailNote}>
                                    / {c.unit}
                                    {c.description ? ` - ${c.description}` : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {svc.specs.length > 0 && (
                          <div className={styles.serviceDetailItem}>
                            <div className={styles.serviceDetailLabel}>Specifications</div>
                            <div className={styles.serviceDetailRows}>
                              {svc.specs.map((s, si) => (
                                <div key={si} className={styles.serviceDetailRow}>
                                  <FontAwesomeIcon
                                    icon={faTag}
                                    className={styles.serviceDetailIcon}
                                  />
                                  <span className={styles.serviceDetailMain}>{s.attribute}:</span>
                                  <span>{s.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export function ExternalProviderSubmit() {
  // ── Gate (identity + password) ──────────────────────────
  const [creator, setCreator] = useState<ExternalCreator | null>(null);

  // ── Page state ──────────────────────────────────────────
  const [pageState, setPageState] = useState<PageState>('form');
  const [closedMsg, setClosedMsg] = useState(false);

  // ── Basic form state ────────────────────────────────────
  const [name, setName] = useState('');
  const [contacts, setContacts] = useState<ProviderContact[]>([
    { nombre: '', email: '', telefono: '', cargo: '', es_principal: true },
  ]);
  const [pais, setPais] = useState(DEFAULT_COUNTRY);
  const [ciudad, setCiudad] = useState(DEFAULT_CITY);
  const [distrito, setDistrito] = useState('');
  const [rating, setRating] = useState(0);

  const { data: countriesData } = useCountries();
  const { data: citiesData } = useCities(pais);
  const { data: districtsData } = useDistricts(pais, ciudad);

  const addContact = () => {
    setContacts((prev) => [...prev, { nombre: '', email: '', telefono: '', cargo: '', es_principal: false }]);
  };

  const removeContact = (index: number) => {
    setContacts((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (!next.some((c) => c.es_principal) && next.length > 0) {
        next[0] = { ...next[0], es_principal: true };
      }
      return next;
    });
  };

  const updateContact = (index: number, field: keyof ProviderContact, value: string | boolean) => {
    setContacts((prev) =>
      prev.map((c, i) => {
        if (i !== index) {
          if (field === 'es_principal' && value === true) return { ...c, es_principal: false };
          return c;
        }
        return { ...c, [field]: value };
      }),
    );
  };

  // ── Document analysis state ─────────────────────────────
  const [documents, setDocuments] = useState<DocumentAnalysis[]>([]);
  const [contextText, setContextText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // ── Sidebar expand/collapse ─────────────────────────────
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  // ── Saved document references ───────────────────────────
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);

  // ── Accepted services (from AI proposals) ───────────────
  const [acceptedServices, setAcceptedServices] = useState<AcceptedService[]>([]);

  // ── Modal ───────────────────────────────────────────────
  const { openModal, closeModal } = useModalStore();

  // ── Beforeunload protection ─────────────────────────────

  const hasFormData =
    name.trim() !== '' ||
    contacts.some((c) => (c.email || '').trim() !== '' || (c.telefono || '').trim() !== '') ||
    documents.length > 0 ||
    acceptedServices.length > 0;

  useEffect(() => {
    if (!hasFormData || pageState !== 'form') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasFormData, pageState]);

  // ── Document analysis ───────────────────────────────────

  const analyzeDocument = useCallback(
    async (localId: string, file: File) => {
      setDocuments((prev) =>
        prev.map((d) => (d.localId === localId ? { ...d, status: 'analyzing' } : d)),
      );
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (contextText) formData.append('additional_context', contextText);
        const res = await api.post<{
          gcs_url: string;
          filename: string;
          summary: string;
          proposals: ProviderProposal;
        }>(API_ROUTES.externalAnalyzeDocument, formData);
        const { gcs_url, filename, summary, proposals } = res.data;
        const fieldAcceptance: Record<string, boolean> = {};
        if (proposals.nombre_comercial) fieldAcceptance.nombre_comercial = false;
        if (proposals.email) fieldAcceptance.email = false;
        if (proposals.telefono) fieldAcceptance.telefono = false;
        if (proposals.ciudad) fieldAcceptance.ciudad = false;
        if (proposals.calificacion != null) fieldAcceptance.calificacion = false;
        const serviceAcceptance: Record<number, boolean> = {};
        proposals.services.forEach((_, i) => {
          serviceAcceptance[i] = true;
        });
        // Auto-expand review when analysis completes
        setDocuments((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? {
                  ...d,
                  status: 'done',
                  gcsUrl: gcs_url,
                  filename,
                  summary,
                  proposals,
                  fieldAcceptance,
                  serviceAcceptance,
                  reviewOpen: true,
                }
              : d,
          ),
        );
        // Auto-add services as phantoms if no existing services
        setAcceptedServices((currentAccepted) => {
          if (currentAccepted.length > 0) return currentAccepted;
          const newServices = (proposals.services || []).map((svc) => ({
            localId: uid(),
            serviceName: svc.service_name,
            serviceId: svc.service_id,
            description: svc.description,
            costs: svc.costs || [],
            specs: svc.specs || [],
            isFromDocument: true,
          }));
          if (newServices.length > 0) {
            toast.success(
              `${newServices.length} service${newServices.length > 1 ? 's' : ''} found — review in Services below`,
            );
          }
          return [...currentAccepted, ...newServices];
        });
      } catch {
        setDocuments((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? { ...d, status: 'error', error: 'Failed to analyze document' }
              : d,
          ),
        );
      }
    },
    [contextText],
  );

  const handleFilesAdded = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;

    // Single file — analyze directly (existing behavior)
    if (arr.length === 1) {
      const file = arr[0];
      if (documents.length >= 10) {
        toast.error('Maximum 10 documents allowed');
        return;
      }
      const localId = uid();
      setDocuments((prev) => [
        ...prev,
        {
          localId,
          file,
          status: 'uploading',
          filename: file.name,
          reviewOpen: false,
          applied: false,
          fieldAcceptance: {},
          fieldDismissed: {},
          serviceAcceptance: {},
          analysisStartTime: Date.now(),
        },
      ]);
      analyzeDocument(localId, file);
      return;
    }

    // Multiple files — ask user how to process
    const validFiles = arr.slice(0, 10 - documents.length);
    if (validFiles.length === 0) {
      toast.error('Maximum 10 documents reached');
      return;
    }

    openModal(
      <div className={styles.unsavedModal}>
        <div className={styles.unsavedModalIcon}>
          <FontAwesomeIcon icon={faFileLines} />
        </div>
        <h3 className={styles.unsavedModalTitle}>{validFiles.length} Documents Selected</h3>
        <p className={styles.unsavedModalText}>How would you like to analyze these documents?</p>
        <div className={styles.batchChoiceCards}>
          <button
            className={styles.batchChoiceCard}
            onClick={() => {
              closeModal();
              handleBatchAnalyze(validFiles);
            }}
          >
            <FontAwesomeIcon icon={faLayerGroup} className={styles.batchChoiceIcon} />
            <span className={styles.batchChoiceTitle}>Analyze as batch</span>
            <span className={styles.batchChoiceDesc}>
              Merge all docs into one analysis. Best when all documents belong to the same provider.
            </span>
          </button>
          <button
            className={styles.batchChoiceCard}
            onClick={() => {
              closeModal();
              handleSequentialAnalyze(validFiles);
            }}
          >
            <FontAwesomeIcon icon={faListOl} className={styles.batchChoiceIcon} />
            <span className={styles.batchChoiceTitle}>Analyze separately</span>
            <span className={styles.batchChoiceDesc}>
              Review each document one at a time. Best when documents are from different providers.
            </span>
          </button>
        </div>
      </div>,
    );
  };

  const handleBatchAnalyze = async (files: File[]) => {
    const localId = uid();
    setDocuments((prev) => [
      ...prev,
      {
        localId,
        file: files[0],
        status: 'uploading' as const,
        filename: `Batch (${files.length} files)`,
        reviewOpen: false,
        applied: false,
        fieldAcceptance: {},
        fieldDismissed: {},
        serviceAcceptance: {},
        analysisStartTime: Date.now(),
      },
    ]);

    setDocuments((prev) =>
      prev.map((d) =>
        d.localId === localId
          ? { ...d, status: 'analyzing' as const, analysisStartTime: Date.now() }
          : d,
      ),
    );

    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      if (contextText) formData.append('additional_context', contextText);

      const res = await api.post<{
        documents: Array<{ gcs_url: string; filename: string; size_bytes: number }>;
        summary: string;
        proposals: ProviderProposal;
      }>(API_ROUTES.externalAnalyzeBatch, formData);

      const { documents: uploadedDocs, summary, proposals } = res.data;

      const fieldAcceptance: Record<string, boolean> = {};
      if (proposals.nombre_comercial) fieldAcceptance.nombre_comercial = false;
      if (proposals.email) fieldAcceptance.email = false;
      if (proposals.telefono) fieldAcceptance.telefono = false;
      if (proposals.ciudad) fieldAcceptance.ciudad = false;
      if (proposals.calificacion != null) fieldAcceptance.calificacion = false;
      const serviceAcceptance: Record<number, boolean> = {};
      proposals.services.forEach((_, i) => {
        serviceAcceptance[i] = true;
      });

      setDocuments((prev) =>
        prev.map((d) =>
          d.localId === localId
            ? {
                ...d,
                status: 'done' as const,
                gcsUrl: uploadedDocs[0]?.gcs_url,
                filename: `Batch (${files.length} files)`,
                summary,
                proposals,
                fieldAcceptance,
                serviceAcceptance,
                reviewOpen: true,
              }
            : d,
        ),
      );

      const newSavedDocs = uploadedDocs.map((doc) => ({
        url: doc.gcs_url,
        name: doc.filename,
        size_bytes: doc.size_bytes,
        uploaded_at: new Date().toISOString(),
      }));
      setSavedDocuments((prev) => [...prev, ...newSavedDocs]);

      setAcceptedServices((currentAccepted) => {
        if (currentAccepted.length > 0) return currentAccepted;
        const newServices = (proposals.services || []).map((svc) => ({
          localId: uid(),
          serviceName: svc.service_name,
          serviceId: svc.service_id,
          description: svc.description,
          costs: svc.costs || [],
          specs: svc.specs || [],
          isFromDocument: true,
        }));
        if (newServices.length > 0) {
          toast.success(
            `${newServices.length} service${newServices.length > 1 ? 's' : ''} found — review in Services below`,
          );
        }
        return [...currentAccepted, ...newServices];
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to analyze documents';
      setDocuments((prev) =>
        prev.map((d) =>
          d.localId === localId ? { ...d, status: 'error' as const, error: msg } : d,
        ),
      );
    }
  };

  const handleSequentialAnalyze = (files: File[]) => {
    const newDocs = files.map((file) => ({
      localId: uid(),
      file,
      status: 'uploading' as const,
      filename: file.name,
      reviewOpen: false,
      applied: false,
      fieldAcceptance: {} as Record<string, boolean>,
      fieldDismissed: {} as Record<string, boolean>,
      serviceAcceptance: {} as Record<number, boolean>,
      analysisStartTime: Date.now(),
    }));

    setDocuments((prev) => [...prev, ...newDocs]);

    for (const doc of newDocs) {
      analyzeDocument(doc.localId, doc.file);
    }
  };

  const handleRetry = (localId: string) => {
    const doc = documents.find((d) => d.localId === localId);
    if (doc) analyzeDocument(localId, doc.file);
  };

  const handleRemove = (localId: string) => {
    setDocuments((prev) => prev.filter((d) => d.localId !== localId));
  };

  const handleToggleReview = (localId: string) => {
    setDocuments((prev) =>
      prev.map((d) => (d.localId === localId ? { ...d, reviewOpen: !d.reviewOpen } : d)),
    );
  };

  const handleToggleField = (localId: string, field: string) => {
    setDocuments((prev) =>
      prev.map((d) =>
        d.localId === localId
          ? { ...d, fieldAcceptance: { ...d.fieldAcceptance, [field]: !d.fieldAcceptance[field] } }
          : d,
      ),
    );
  };

  const handleDismissField = (localId: string, field: string) => {
    setDocuments((prev) =>
      prev.map((d) =>
        d.localId === localId
          ? {
              ...d,
              fieldDismissed: { ...d.fieldDismissed, [field]: true },
              fieldAcceptance: { ...d.fieldAcceptance, [field]: false },
            }
          : d,
      ),
    );
  };

  const handleToggleService = (localId: string, idx: number) => {
    setDocuments((prev) =>
      prev.map((d) =>
        d.localId === localId
          ? {
              ...d,
              serviceAcceptance: { ...d.serviceAcceptance, [idx]: !d.serviceAcceptance[idx] },
            }
          : d,
      ),
    );
  };

  const handleApplyField = (fieldKey: string, value: string | number | null) => {
    switch (fieldKey) {
      case 'nombre_comercial':
        setName((value as string) || '');
        break;
      case 'ciudad':
        setCiudad((value as string) || DEFAULT_CITY);
        break;
      case 'calificacion':
        setRating((value as number) || 0);
        break;
    }
  };

  const handleApplyAll = (doc: DocumentAnalysis) => {
    if (!doc.proposals) return;
    const { proposals, serviceAcceptance } = doc;
    const fieldDismissed = doc.fieldDismissed || {};

    // Apply all non-dismissed basic fields
    if (!fieldDismissed.nombre_comercial && proposals.nombre_comercial)
      setName(proposals.nombre_comercial);
    if (proposals.contactos?.length) {
      setContacts(proposals.contactos.map((c, i) => ({
        nombre: c.nombre || '',
        email: c.email || '',
        telefono: c.telefono || '',
        cargo: c.cargo || '',
        es_principal: i === 0,
      })));
    } else if (proposals.email || proposals.telefono) {
      setContacts([{
        nombre: '',
        email: proposals.email || '',
        telefono: proposals.telefono || '',
        cargo: '',
        es_principal: true,
      }]);
    }
    if (!fieldDismissed.ciudad && proposals.ciudad) setCiudad(proposals.ciudad);
    if (!fieldDismissed.calificacion && proposals.calificacion != null)
      setRating(proposals.calificacion);

    // Mark all non-dismissed fields as accepted
    setDocuments((prev) =>
      prev.map((d) => {
        if (d.localId !== doc.localId) return d;
        const updatedAcceptance = { ...d.fieldAcceptance };
        for (const key of Object.keys(updatedAcceptance)) {
          if (!fieldDismissed[key]) updatedAcceptance[key] = true;
        }
        // Mark the document as applied (keep it visible but dimmed)
        return { ...d, fieldAcceptance: updatedAcceptance, applied: true, reviewOpen: false };
      }),
    );

    // Queue accepted services
    const acceptedSvcs = (proposals.services || [])
      .map((svc, originalIdx) => ({ svc, originalIdx }))
      .filter(({ originalIdx }) => serviceAcceptance?.[originalIdx] !== false);

    if (acceptedSvcs.length > 0) {
      setAcceptedServices((prev) => {
        const existingIds = new Set(prev.filter((s) => s.serviceId).map((s) => s.serviceId));
        const newSvcs = acceptedSvcs
          .filter(({ svc }) => !svc.service_id || !existingIds.has(svc.service_id))
          .map(({ svc }) => ({
            localId: uid(),
            serviceName: svc.service_name,
            serviceId: svc.service_id,
            description: svc.description,
            costs: svc.costs || [],
            specs: svc.specs || [],
            isFromDocument: true,
          }));
        return [...prev, ...newSvcs];
      });
    }

    // Save the document reference (keep doc in documents[] but mark applied)
    if (doc.gcsUrl) {
      const newDoc: SavedDocument = {
        url: doc.gcsUrl,
        name: doc.filename,
        size_bytes: doc.file.size,
        uploaded_at: new Date().toISOString(),
      };
      setSavedDocuments((prev) => [...prev, newDoc]);
    }

    const fieldCount = Object.keys(doc.fieldAcceptance || {}).filter(
      (k) => !fieldDismissed[k],
    ).length;
    const svcCount = acceptedSvcs.length;
    const parts: string[] = [];
    if (fieldCount > 0) parts.push(`${fieldCount} field${fieldCount > 1 ? 's' : ''} applied`);
    if (svcCount > 0) parts.push(`${svcCount} service${svcCount > 1 ? 's' : ''} added`);
    parts.push('document saved');
    toast.success(parts.join(' · '));
  };

  // ── Service editing ─────────────────────────────────

  const [expandedServiceIds, setExpandedServiceIds] = useState<Set<string>>(new Set());

  const toggleServiceExpanded = (localId: string) => {
    setExpandedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  const handleAddService = () => {
    const id = uid();
    setAcceptedServices((prev) => [
      ...prev,
      {
        localId: id,
        serviceName: '',
        serviceId: null,
        description: '',
        costs: [],
        specs: [],
      },
    ]);
    setExpandedServiceIds((prev) => new Set(prev).add(id));
  };

  const updateService = (localId: string, patch: Partial<AcceptedService>) => {
    setAcceptedServices((prev) =>
      prev.map((s) => (s.localId === localId ? { ...s, ...patch } : s)),
    );
  };

  const addCostToService = (localId: string) => {
    setAcceptedServices((prev) =>
      prev.map((s) =>
        s.localId === localId
          ? {
              ...s,
              costs: [
                ...s.costs,
                { unit: '', pricing_unit_id: null, price: 0, currency: 'USD', description: '' },
              ],
            }
          : s,
      ),
    );
  };

  const updateCostInService = (localId: string, costIdx: number, patch: Partial<ProposedCost>) => {
    setAcceptedServices((prev) =>
      prev.map((s) => {
        if (s.localId !== localId) return s;
        const costs = s.costs.map((c, i) => (i === costIdx ? { ...c, ...patch } : c));
        return { ...s, costs };
      }),
    );
  };

  const removeCostFromService = (localId: string, costIdx: number) => {
    setAcceptedServices((prev) =>
      prev.map((s) => {
        if (s.localId !== localId) return s;
        return { ...s, costs: s.costs.filter((_, i) => i !== costIdx) };
      }),
    );
  };

  const addSpecToService = (localId: string) => {
    setAcceptedServices((prev) =>
      prev.map((s) =>
        s.localId === localId
          ? { ...s, specs: [...s.specs, { attribute: '', attribute_id: null, value: '' }] }
          : s,
      ),
    );
  };

  const updateSpecInService = (localId: string, specIdx: number, patch: Partial<ProposedSpec>) => {
    setAcceptedServices((prev) =>
      prev.map((s) => {
        if (s.localId !== localId) return s;
        const specs = s.specs.map((sp, i) => (i === specIdx ? { ...sp, ...patch } : sp));
        return { ...s, specs };
      }),
    );
  };

  const removeSpecFromService = (localId: string, specIdx: number) => {
    setAcceptedServices((prev) =>
      prev.map((s) => {
        if (s.localId !== localId) return s;
        return { ...s, specs: s.specs.filter((_, i) => i !== specIdx) };
      }),
    );
  };

  // ── Drag and drop ───────────────────────────────────────

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) handleFilesAdded(e.dataTransfer.files);
  };

  // ── Submit (with confirmation modal) ────────────────────

  // Use a ref so the modal always calls the latest version (avoids stale closure)
  const doSubmitRef = useRef<() => Promise<void>>(() => Promise.resolve());
  doSubmitRef.current = async () => {
    const allDocs: SavedDocument[] = [
      ...savedDocuments,
      ...documents
        .filter((d) => d.status === 'done' && d.gcsUrl && !d.applied)
        .map((d) => ({
          url: d.gcsUrl!,
          name: d.filename,
          size_bytes: d.file.size,
          uploaded_at: new Date().toISOString(),
        })),
    ];

    const payload = {
      nombre_comercial: name,
      contacts: contacts.map((c) => ({
        nombre: c.nombre || null,
        email: c.email || null,
        telefono: c.telefono || null,
        cargo: c.cargo || null,
        es_principal: c.es_principal || false,
      })),
      pais,
      ciudad,
      distrito: distrito || null,
      calificacion: rating || null,
      documents: allDocs,
      temp_creator: creator,
      services: acceptedServices
        .filter(
          (svc, i, arr) =>
            !svc.serviceId || arr.findIndex((s) => s.serviceId === svc.serviceId) === i,
        )
        .map((svc) => ({
          service_name: svc.serviceName,
          service_id: svc.serviceId,
          description: svc.description,
          costs: svc.costs.map((c) => ({
            pricing_unit_id: c.pricing_unit_id,
            unit: c.unit,
            price: c.price,
            currency: c.currency,
            description: c.description,
          })),
          specs: svc.specs.map((s) => ({
            attribute_id: s.attribute_id,
            attribute: s.attribute,
            value: s.value,
          })),
        })),
    };

    await api.post(API_ROUTES.externalSubmitProvider, payload);
    closeModal();
    setPageState('thanks');
  };

  const handleConfirmSubmit = async () => {
    try {
      await doSubmitRef.current();
    } catch (err) {
      toast.error('Failed to submit provider. Please try again.');
      throw err; // Re-throw so modal resets its loading state
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Provider name is required');
      return;
    }

    const unappliedDocs = documents.filter((d) => d.status === 'done' && !d.applied);

    const proceedToConfirm = () => {
      const totalDocs =
        savedDocuments.length + documents.filter((d) => d.status === 'done' && d.gcsUrl).length;
      openModal(
        <SubmitConfirmModal
          providerName={name}
          serviceCount={acceptedServices.length}
          documentCount={totalDocs}
          onConfirm={handleConfirmSubmit}
          handleClose={closeModal}
        />,
      );
    };

    if (unappliedDocs.length > 0) {
      openModal(
        <div className={styles.unsavedModal}>
          <div className={styles.unsavedModalIcon}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h3 className={styles.unsavedModalTitle}>Unapplied Documents</h3>
          <p className={styles.unsavedModalText}>
            You have {unappliedDocs.length} analyzed document{unappliedDocs.length > 1 ? 's' : ''}{' '}
            with proposals that haven't been applied. Their proposed services won't be submitted.
          </p>
          <div className={styles.unsavedModalActions}>
            <Button variant="secondary" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={() => {
                closeModal();
                setTimeout(proceedToConfirm, 250);
              }}
            >
              Continue Anyway
            </Button>
          </div>
        </div>,
      );
      return;
    }

    proceedToConfirm();
  };

  // ── Reset for another submission ────────────────────────

  const handleReset = () => {
    setName('');
    setContacts([{ nombre: '', email: '', telefono: '', cargo: '', es_principal: true }]);
    setCiudad(DEFAULT_CITY);
    setRating(0);
    setDocuments([]);
    setContextText('');
    setSavedDocuments([]);
    setAcceptedServices([]);
    setPageState('form');
    setClosedMsg(false);
  };

  const handleClosePage = () => {
    window.close();
    // window.close() may not work if the page wasn't opened via script
    setTimeout(() => setClosedMsg(true), 300);
  };

  // ── Saved document remove ───────────────────────────────

  const handleRemoveSavedDoc = (index: number) => {
    setSavedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Render ──────────────────────────────────────────────

  if (!creator) {
    return <GateModal onUnlock={setCreator} />;
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <img src={LOGO.src} alt={LOGO.alt} className={styles.logo} />
        <span className={styles.headerTitle}>
          <span className={styles.headerAccent}>{PROJECT_NAME}</span> — Provider Registration
        </span>
        <span className={styles.creatorBadge}>{creator.name}</span>
      </header>

      {/* Submitting state */}
      {pageState === 'submitting' && (
        <div className={styles.submittingOverlay}>
          <FontAwesomeIcon icon={faSpinner} spin className={styles.submittingSpinner} />
          <div className={styles.submittingText}>Submitting your information...</div>
          <div className={styles.submittingHint}>
            This may take a moment. Please do not close this page.
          </div>
        </div>
      )}

      {/* Thank you state */}
      {pageState === 'thanks' && (
        <div className={styles.thankYou}>
          <div className={styles.thankYouIcon}>
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <h1 className={styles.thankYouTitle}>Thank you for your submission!</h1>
          <p className={styles.thankYouMessage}>
            Your provider information has been received and will be reviewed by our team. We will
            get in touch if we need any additional details.
          </p>
          <div className={styles.thankYouActions}>
            <Button variant="primary" onClick={handleReset}>
              Add Another Provider
            </Button>
            <Button variant="secondary" onClick={handleClosePage}>
              Close Page
            </Button>
          </div>
          {closedMsg && <p className={styles.closedMsg}>You can safely close this tab.</p>}
        </div>
      )}

      {/* Form state */}
      {pageState === 'form' && (
        <div className={styles.editorBody}>
          {/* ══════════ LEFT PANEL ══════════ */}
          <div className={styles.formPanel}>
            {/* Basic Info */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span>Basic Information</span>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.fieldFull}>
                  <Input
                    name="name"
                    label="Provider Name *"
                    value={name}
                    setValue={setName}
                    placeholder="e.g. Hotel Los Delfines"
                    isRequired
                  />
                </div>
                <div className={styles.fieldFull}>
                  <div className={styles.contactsSection}>
                    <div className={styles.contactsHeader}>
                      <span className={styles.contactsTitle}>Contacts</span>
                      <button type="button" className={styles.addContactBtn} onClick={addContact}>
                        <FontAwesomeIcon icon={faPlus} /> Add Contact
                      </button>
                    </div>
                    {contacts.map((contact, idx) => (
                      <div key={idx} className={styles.contactRow}>
                        <div className={styles.contactFields}>
                          <Input
                            name={`contact-name-${idx}`}
                            label="Name"
                            value={contact.nombre || ''}
                            setValue={(v) => updateContact(idx, 'nombre', v)}
                            placeholder="Contact person"
                          />
                          <Input
                            name={`contact-email-${idx}`}
                            label="Email"
                            type="email"
                            value={contact.email || ''}
                            setValue={(v) => updateContact(idx, 'email', v)}
                            placeholder="contact@provider.com"
                          />
                          <Input
                            name={`contact-phone-${idx}`}
                            label="Phone"
                            value={contact.telefono || ''}
                            setValue={(v) => updateContact(idx, 'telefono', v)}
                            placeholder="+51 999 000 000"
                          />
                          <Input
                            name={`contact-role-${idx}`}
                            label="Role"
                            value={contact.cargo || ''}
                            setValue={(v) => updateContact(idx, 'cargo', v)}
                            placeholder="e.g. Sales Manager"
                          />
                        </div>
                        <div className={styles.contactActions}>
                          <label className={styles.primaryLabel}>
                            <input
                              type="radio"
                              name="primary-contact"
                              checked={contact.es_principal || false}
                              onChange={() => updateContact(idx, 'es_principal', true)}
                            />
                            Primary
                          </label>
                          {contacts.length > 1 && (
                            <button
                              type="button"
                              className={styles.removeContactBtn}
                              onClick={() => removeContact(idx)}
                              title="Remove contact"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Select
                  label="Country"
                  value={pais}
                  onChange={(e) => { setPais(e.target.value); setCiudad(''); setDistrito(''); }}
                  options={countriesData ?? []}
                  searchable
                />
                <Select
                  label="City"
                  value={ciudad}
                  onChange={(e) => { setCiudad(e.target.value); setDistrito(''); }}
                  options={citiesData ?? []}
                  placeholder="Select city..."
                  searchable
                />
                <Select
                  label="District"
                  value={distrito}
                  onChange={(e) => setDistrito(e.target.value)}
                  options={districtsData ?? []}
                  placeholder="Select district..."
                  searchable
                />
                <div />
              </div>
              <div>
                <label className={styles.fieldLabel}>Rating</label>
                <div className={styles.stars}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={styles.starBtn}
                      onClick={() => setRating(n === rating ? 0 : n)}
                    >
                      <FontAwesomeIcon
                        icon={faStar}
                        className={n <= rating ? styles.starFilled : styles.starEmpty}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Services */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span>Services ({acceptedServices.length})</span>
                <button className={styles.addServiceBtn} onClick={handleAddService}>
                  <FontAwesomeIcon icon={faPlus} /> Add Service
                </button>
              </div>

              {acceptedServices.length > 0 ? (
                <div className={styles.servicesList}>
                  {acceptedServices.map((svc) => {
                    const isExpanded = expandedServiceIds.has(svc.localId);
                    return (
                      <div key={svc.localId} className={styles.serviceCard}>
                        <div className={styles.serviceCardHeader}>
                          <div
                            className={styles.serviceCardTitle}
                            onClick={() => toggleServiceExpanded(svc.localId)}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronUp : faChevronDown}
                              className={styles.serviceExpandIcon}
                            />
                            <span className={styles.serviceName}>
                              {svc.serviceName || 'New Service'}
                            </span>
                            {svc.isFromDocument && (
                              <span className={styles.fromDocBadge}>from doc</span>
                            )}
                          </div>
                          <div className={styles.serviceCardBtns}>
                            <button className={styles.pendingAcceptBtn} title="Accepted" disabled>
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button
                              className={styles.iconBtn}
                              title="Edit"
                              onClick={() => toggleServiceExpanded(svc.localId)}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                            <button
                              className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                              title="Remove"
                              onClick={() =>
                                setAcceptedServices((prev) =>
                                  prev.filter((s) => s.localId !== svc.localId),
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>

                        {!isExpanded && (
                          <>
                            {svc.description && (
                              <p className={styles.serviceDesc}>{svc.description}</p>
                            )}
                            <div className={styles.servicePills}>
                              {svc.costs.map((c, i) => (
                                <span key={i} className={styles.costPill}>
                                  <FontAwesomeIcon icon={faDollarSign} /> {c.price} {c.currency} /{' '}
                                  {c.unit}
                                </span>
                              ))}
                              {svc.specs.map((s, i) => (
                                <span key={i} className={styles.specPill}>
                                  <FontAwesomeIcon icon={faTag} /> {s.attribute}: {s.value}
                                </span>
                              ))}
                            </div>
                            {svc.costs.length === 0 &&
                              svc.specs.length === 0 &&
                              !svc.description && (
                                <p className={styles.serviceDescEmpty}>
                                  No details — click to expand and add
                                </p>
                              )}
                          </>
                        )}

                        {isExpanded && (
                          <div className={styles.serviceEditBody}>
                            <div className={styles.serviceEditGrid}>
                              <Input
                                name={`svc-name-${svc.localId}`}
                                label="Service Name"
                                value={svc.serviceName}
                                setValue={(v) => updateService(svc.localId, { serviceName: v })}
                                placeholder="e.g. Catering, Venue, Photography"
                              />
                              <Input
                                name={`svc-desc-${svc.localId}`}
                                label="Description"
                                value={svc.description}
                                setValue={(v) => updateService(svc.localId, { description: v })}
                                placeholder="Brief description of this service"
                              />
                            </div>

                            {/* Costs */}
                            <div className={styles.serviceEditSection}>
                              <div className={styles.serviceEditSectionHeader}>
                                <span>Costs ({svc.costs.length})</span>
                                <button
                                  className={styles.addItemBtn}
                                  onClick={() => addCostToService(svc.localId)}
                                >
                                  <FontAwesomeIcon icon={faPlus} /> Add
                                </button>
                              </div>
                              {svc.costs.map((c, ci) => (
                                <div key={ci} className={styles.editableRow}>
                                  <Input
                                    name={`cost-price-${svc.localId}-${ci}`}
                                    label="Price"
                                    type="number"
                                    value={String(c.price)}
                                    setValue={(v) =>
                                      updateCostInService(svc.localId, ci, {
                                        price: parseFloat(v) || 0,
                                      })
                                    }
                                    placeholder="0"
                                  />
                                  <Input
                                    name={`cost-currency-${svc.localId}-${ci}`}
                                    label="Currency"
                                    value={c.currency}
                                    setValue={(v) =>
                                      updateCostInService(svc.localId, ci, { currency: v })
                                    }
                                    placeholder="USD"
                                  />
                                  <Input
                                    name={`cost-unit-${svc.localId}-${ci}`}
                                    label="Unit"
                                    value={c.unit}
                                    setValue={(v) =>
                                      updateCostInService(svc.localId, ci, { unit: v })
                                    }
                                    placeholder="per hour"
                                  />
                                  <Input
                                    name={`cost-desc-${svc.localId}-${ci}`}
                                    label="Note"
                                    value={c.description}
                                    setValue={(v) =>
                                      updateCostInService(svc.localId, ci, { description: v })
                                    }
                                    placeholder="Optional note"
                                  />
                                  <button
                                    className={`${styles.iconBtn} ${styles.iconBtnDanger} ${styles.rowDeleteBtn}`}
                                    onClick={() => removeCostFromService(svc.localId, ci)}
                                    title="Remove cost"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </div>
                              ))}
                            </div>

                            {/* Specs */}
                            <div className={styles.serviceEditSection}>
                              <div className={styles.serviceEditSectionHeader}>
                                <span>Specifications ({svc.specs.length})</span>
                                <button
                                  className={styles.addItemBtn}
                                  onClick={() => addSpecToService(svc.localId)}
                                >
                                  <FontAwesomeIcon icon={faPlus} /> Add
                                </button>
                              </div>
                              {svc.specs.map((sp, si) => (
                                <div key={si} className={styles.editableRow}>
                                  <Input
                                    name={`spec-attr-${svc.localId}-${si}`}
                                    label="Attribute"
                                    value={sp.attribute}
                                    setValue={(v) =>
                                      updateSpecInService(svc.localId, si, { attribute: v })
                                    }
                                    placeholder="e.g. Capacity"
                                  />
                                  <Input
                                    name={`spec-val-${svc.localId}-${si}`}
                                    label="Value"
                                    value={sp.value}
                                    setValue={(v) =>
                                      updateSpecInService(svc.localId, si, { value: v })
                                    }
                                    placeholder="e.g. 200 people"
                                  />
                                  <button
                                    className={`${styles.iconBtn} ${styles.iconBtnDanger} ${styles.rowDeleteBtn}`}
                                    onClick={() => removeSpecFromService(svc.localId, si)}
                                    title="Remove spec"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptyServices}>
                  <p>No services added yet</p>
                  <p className={styles.emptyHint}>
                    Upload documents on the right panel and AI will propose services, or add
                    services manually.
                  </p>
                </div>
              )}
            </section>

            {/* Submit */}
            <div className={styles.submitRow}>
              <Button variant="primary" size="lg" onClick={handleSubmit} disabled={!name.trim()}>
                Submit Provider
              </Button>
            </div>
          </div>

          {/* ══════════ RIGHT PANEL (AI Sidebar) ══════════ */}
          {sidebarExpanded && (
            <div className={styles.sidebarOverlay} onClick={() => setSidebarExpanded(false)} />
          )}

          <div className={`${styles.aiSidebar} ${sidebarExpanded ? styles.aiSidebarExpanded : ''}`}>
            {/* Sidebar header with expand toggle */}
            <div className={styles.sidebarHeader}>
              <span className={styles.sidebarTitle}>
                <FontAwesomeIcon icon={faRobot} /> AI Document Analysis
              </span>
              <button
                className={styles.sidebarExpandBtn}
                onClick={() => setSidebarExpanded((prev) => !prev)}
                title={sidebarExpanded ? 'Collapse panel' : 'Expand panel for better review'}
              >
                <FontAwesomeIcon icon={sidebarExpanded ? faCompress : faExpand} />
                <span className={styles.sidebarExpandLabel}>
                  {sidebarExpanded ? 'Collapse' : 'Expand'}
                </span>
              </button>
            </div>

            {/* Workflow guidance banner */}
            <div className={styles.workflowBanner}>
              <FontAwesomeIcon icon={faInfoCircle} className={styles.workflowBannerIcon} />
              <span>
                Upload provider documents (price lists, brochures) and our AI will extract services,
                pricing, and details. Review the proposals and apply what's correct.
              </span>
            </div>

            {/* Upload section */}
            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeader}>Upload Documents</div>
              <div
                className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faUpload} className={styles.dropIcon} />
                <span className={styles.dropText}>Drop files here or click to upload</span>
                <span className={styles.dropHint}>
                  PDF, DOCX, TXT, PNG, JPG, WEBP (max 10 files)
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.length) {
                    handleFilesAdded(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
              <textarea
                className={styles.contextArea}
                rows={2}
                placeholder="Optional: add context about this provider..."
                value={contextText}
                onChange={(e) => setContextText(e.target.value)}
              />
            </div>

            {/* Analyzed documents */}
            {documents.length > 0 && (
              <div className={styles.sidebarSection}>
                <div className={styles.sidebarSectionHeader}>
                  Analyzed Documents ({documents.length})
                </div>
                <div className={styles.docList}>
                  {documents.map((doc) => (
                    <DocumentCard
                      key={doc.localId}
                      doc={doc}
                      onRetry={handleRetry}
                      onRemove={handleRemove}
                      onToggleReview={handleToggleReview}
                      onToggleField={handleToggleField}
                      onDismissField={handleDismissField}
                      onApplyAll={handleApplyAll}
                      onToggleService={handleToggleService}
                      onApplyField={handleApplyField}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Saved documents */}
            {savedDocuments.length > 0 && (
              <div className={styles.sidebarSection}>
                <div className={styles.sidebarSectionHeader}>
                  Uploaded Documents ({savedDocuments.length})
                </div>
                <div className={styles.savedDocList}>
                  {savedDocuments.map((doc, i) => (
                    <div key={i} className={styles.savedDocItem}>
                      <FontAwesomeIcon icon={faFile} className={styles.savedDocIcon} />
                      <span className={styles.savedDocName}>{doc.name}</span>
                      <button
                        className={styles.savedDocRemoveBtn}
                        onClick={() => handleRemoveSavedDoc(i)}
                        title="Remove document"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
